import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser, userSelector } from "../../state/slices/user-slice";
import { QubesUserRole } from "../../qubes-client/model/model";
import { QubesAPI } from "../../qubes-client/api";
import { Paper, Typography } from "@mui/material";
import Cookies from "js-cookie";
import { AuthTokenManager } from "../../services/auth-token-manager";
import { ROUTES } from "../../routes/paths";

/**
 * Module-level promise to coordinate initialization across multiple mounts.
 * If one AuthWall instance is already performing initialization, others
 * should wait for it to complete instead of short-circuiting and causing
 * premature redirects.
 *
 * MUST be reset whenever the session lifecycle transitions (post-login
 * handoff, logout) — otherwise a "no session" verdict cached here will
 * stick across the boundary and bounce the freshly-authenticated user
 * straight back to the login page. See resetAuthWallInit().
 */
let __authWallInitializationPromise: Promise<boolean> | null = null;
/**
 * Module-level flag to avoid running the expensive initialization more than
 * once per app lifecycle. This survives StrictMode's mount/unmount remount
 * cycles during development.
 */
let __authWallInitializationResult: boolean | null = null;
/**
 * True when the last initialization run got NO VERDICT (backend unreachable, a
 * 5xx or network failure on the session ping — not a 401/403). Module-level so
 * an instance that consumes a cached/coordinated result knows the result is
 * provenance-unverified and must not reroute on it.
 */
let __authWallUnverifiable = false;

/**
 * resetAuthWallInit clears the module-level coordination state so that
 * the next AuthWall mount runs a fresh session check. Call this on any
 * session-lifecycle transition:
 *   - successful login (auth-handoff sets the token + dispatches setUser)
 *   - logout (NavMenu clears the token)
 * Without this, a stale "no session" verdict from a pre-login AuthWall
 * mount will short-circuit the post-login destination's AuthWall and
 * loop the user back to /auth/login.
 */
// This resets AuthWall's module-level initialization state (below); it is
// intentionally co-located with that state rather than split into another module.
// eslint-disable-next-line react-refresh/only-export-components
export function resetAuthWallInit(): void {
  __authWallInitializationPromise = null;
  __authWallInitializationResult = null;
  __authWallUnverifiable = false;
}

/**
 * A ping failure is only an AUTH verdict when the server actually judged the
 * credential: an HTTP 401/403, or a 200 whose body says loggedIn=false (ping
 * throws "User is not logged in" for that). A 5xx or a network failure means
 * the BACKEND WAS UNREACHABLE — no verdict at all. Treating those as "logged
 * out" cleared working credentials and hard-redirected live tabs to the login
 * page whenever a gateway/proxy blipped mid-session.
 * BUG-20260802-react-transient-outage-hard-navigates-to-login.
 */
const isDefinitiveAuthRejection = (err: unknown): boolean => {
  const s = (err as { response?: { status?: number } })?.response?.status;
  if (s === 401 || s === 403) return true;
  if (err instanceof Error && err.message === "User is not logged in")
    return true;
  return false;
};

/** How long to wait before re-asking when the session check was unverifiable. */
const UNVERIFIABLE_RECHECK_MS = 5000;

// Wrap any elements that you want protected, pass roles and message through props
const AuthWall: React.FC<{ children: ReactNode; roles: string[] }> = ({
  children,
  roles,
}) => {
  const dispatch = useDispatch();

  const loginPagePath: string = ROUTES.AUTH.LOGIN;
  const [initialized, setInitialized] = useState(false);
  // True when the session check could not reach a verdict (backend down): the
  // wall then renders the app instead of rerouting — destroying the tab is the
  // one response guaranteed wrong on a transient outage — and re-checks until
  // it gets a definitive answer.
  const [unverifiable, setUnverifiable] = useState(false);

  /**
   * The first time this component is loaded, we want to see if there is already an existing
   * session available. If so, confirm it is active and use it, otherwise route the user to
   * the login page
   */
  useEffect(() => {
    // Set by any check whose ping failed WITHOUT an auth verdict (backend
    // unreachable). Drives the render-children-and-recheck path instead of the
    // reroute; reset at the start of every run.
    let sawTransient = false;

    const checkSessUrlParm = async (qd: Record<string, string[]>): Promise<boolean> => {
      let haveActiveUser = false;
      try {
        QubesAPI.get().setAuthToken(qd.sess[0]);
        const user = await QubesAPI.get().ping();
        dispatch(setUser({ user }));
        haveActiveUser = true;
        console.info(`Existing session is active`);
        if (window.location.pathname === loginPagePath)
          window.location.replace("/");
      } catch (err) {
        if (isDefinitiveAuthRejection(err)) {
          //the error is already logged, so we'll just log what's relevant to our context
          console.info("The user is not logged in");
        } else {
          console.info("Session check unreachable (sess parm) — no verdict");
          sawTransient = true;
        }
      }
      setInitialized(true);
      return haveActiveUser;
    };

    const checkSessCookie = async (sessCookie: string): Promise<boolean> => {
      let haveActiveUser = false;
      console.info(`Existing cookie found | ${sessCookie}`);
      try {
        const user = await QubesAPI.get().ping();
        dispatch(setUser({ user }));
        console.info(`Existing cookie is active`);
        haveActiveUser = true;
      } catch (err) {
        if (isDefinitiveAuthRejection(err)) {
          //the error is already logged, so we'll just log what's relevant to our context
          console.info("The user is not logged in");

          Cookies.remove("SESS");
          Cookies.remove("SESS-US"); //remove the user-switch cookie

          //TODO where do we send the user?????
        } else {
          // Backend unreachable: the cookie was never judged — keep it.
          console.info("Session check unreachable (cookie) — no verdict");
          sawTransient = true;
        }
      }
      setInitialized(true);
      return haveActiveUser;
    };

    const checkLocalStorage = async (
      localStorageToken: string
    ): Promise<boolean> => {
      const haveActiveUser = false;
      console.info(`Existing session found | ${localStorageToken}`);
      try {
        QubesAPI.get().setAuthToken(localStorageToken);
        const user = await QubesAPI.get().ping();
        dispatch(setUser({ user }));
        console.info(`Existing session is active`);
        if (window.location.pathname === loginPagePath)
          window.location.replace("/");
      } catch (err) {
        if (isDefinitiveAuthRejection(err)) {
          //the error is already logged, so we'll just log what's relevant to our context
          console.info("The user is not logged in");
          //token rejected — clear the persistent store so we don't loop on it
          AuthTokenManager.getInstance().clearToken();
        } else {
          // Backend unreachable: the token was never judged — keep it. Clearing
          // here made a 2s gateway blip destroy a valid 24h session.
          console.info("Session check unreachable (localStorage) — no verdict");
          sawTransient = true;
        }
      }
      setInitialized(true);
      return haveActiveUser;
    };

    const checkIfExistingSession = async () => {
      // If another instance is already running initialization, wait for it
      // to finish and reuse its result. This prevents races where a
      // second AuthWall would mark itself initialized before the user
      // state has been populated and cause a premature redirect.
      if (__authWallInitializationPromise) {
        try {
          const result = await __authWallInitializationPromise;
          __authWallInitializationResult = result;
          // A coordinated result produced under an unreachable backend is not
          // a verdict — inherit the unverifiable state so this instance also
          // renders instead of rerouting.
          if (__authWallUnverifiable) setUnverifiable(true);
          setInitialized(true);
          return;
        } catch (_e) {
          // fall through and attempt initialization ourselves
        }
      }

      // No existing initializer — create a coordinating promise so other
      // instances can await our work.
      let resolver: (v: boolean) => void = () => {};
      __authWallInitializationPromise = new Promise<boolean>((resolve) => {
        resolver = resolve;
      });
      try {
        //Deployed Server path
        let haveActiveUser = false;
        const sessCookie = Cookies.get("SESS");

        // Check AuthTokenManager's localStorage token
        const localStorageToken = AuthTokenManager.getInstance().getToken();

        // Get the decoded url parms if they exist
        const qd: Record<string, string[]> = {};
        window.location.search
          ?.substring(1)
          ?.split("&")
          ?.forEach((item) => {
            const k = item.split("=")[0],
              v = decodeURIComponent(item.split("=")[1]);
            if (k in qd) {
              qd[k].push(v);
            } else {
              qd[k] = [v];
            }
          });

        // if we have a sess variable, confirm it is active
        if (qd.sess) {
          haveActiveUser = await checkSessUrlParm(qd);
        }

        if (!haveActiveUser && sessCookie) {
          haveActiveUser = await checkSessCookie(sessCookie);
        }

        // Check AuthTokenManager's localStorage (persists across tab closures)
        if (!haveActiveUser && localStorageToken) {
          console.info(
            `Existing localStorage token found from AuthTokenManager`
          );
          haveActiveUser = await checkLocalStorage(localStorageToken);
        }

        // Record whether this run reached a verdict at all: a transient
        // (unreachable-backend) failure means the outcome below is provisional
        // and must drive render-and-recheck, never a reroute.
        __authWallUnverifiable = sawTransient;
        setUnverifiable(sawTransient);

        if (!sessCookie && !localStorageToken) {
          console.info("No existing session found");
          __authWallInitializationResult = false;
          resolver(false);
          setInitialized(true);
        } else {
          // If we reached here, one of the checks likely set the user and
          // marked haveActiveUser = true; ensure we resolve the promise.
          __authWallInitializationResult = true;
          resolver(true);
          setInitialized(true);
        }
      } catch (err) {
        console.error(err);
        if (resolver) resolver(false);
        __authWallInitializationResult = false;
        setInitialized(true);
      }
    };
    // Run the check; while the outcome is unverifiable (backend unreachable),
    // re-run on a timer until a definitive verdict lands — at which point the
    // normal paths take over (user set and granted, or reroute to login).
    let disposed = false;
    let recheckTimer: ReturnType<typeof setTimeout> | undefined;
    const runSessionCheck = async () => {
      sawTransient = false;
      await checkIfExistingSession();
      if (disposed) return;
      if (__authWallUnverifiable) {
        recheckTimer = setTimeout(() => {
          resetAuthWallInit();
          void runSessionCheck();
        }, UNVERIFIABLE_RECHECK_MS);
      }
    };
    void runSessionCheck();
    return () => {
      disposed = true;
      if (recheckTimer) clearTimeout(recheckTimer);
    };
    //these dependencies should never change so as to be certain this method only runs once
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional run-once auth-init; dispatch/loginPagePath are stable and re-running would re-trigger the session check
  }, []);

  const user = useSelector(userSelector);

  const access =
    user?.roles && Array.isArray(user?.roles) && user?.roles.length > 0
      ? user?.roles
      : [];

  const granted = access.some((role: QubesUserRole) =>
    roles.find((r) => r === role.name)
  );

  if (!initialized)
    return (
      <Paper elevation={0} sx={{ p: 1 }}>
        <Typography>Initializing...</Typography>
      </Paper>
    );
  else if (window.location.pathname === loginPagePath || granted) {
    if (!granted) console.debug("already headed to login page");
    return <div className="_auth-wall">{children}</div>;
  } else if (unverifiable) {
    // No verdict on the session — the backend was unreachable when we asked.
    // Render the app: the reroute below is a HARD navigation that would
    // destroy a live tab (and every ws subscription in it) on the strength of
    // a proxy 500. The recheck timer above keeps asking; a definitive
    // rejection still reroutes, a definitive success populates the user.
    console.debug("auth-wall: session unverifiable — rendering without verdict");
    return <div className="_auth-wall">{children}</div>;
  } else {
    console.debug("reroute to login page");
    const currentPath = window.location.pathname + window.location.search;

    // Save redirect path to sessionStorage (but not if it's login/auth-handoff to prevent loops)
    if (currentPath !== loginPagePath && currentPath !== ROUTES.AUTH.HANDOFF) {
      sessionStorage.setItem("postLoginRedirect", currentPath);
    }

    window.location.replace(loginPagePath);
    return <></>;
  }
};

export default AuthWall;
